import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, isString } from 'lodash';
import { onlyDigits } from '../helpers/string';
import { useLoading } from '../helpers/useLoading';
import { Address, CEPService } from '../shared/cep';
import { FormLabel } from './UI-CHAKRA/FormLabel';
import { InputGroup } from './UI-CHAKRA/Input/InputGroup';
import { MaskedInput, MASKS } from './UI-CHAKRA/Input/MaskedInput';
import { FormControl } from './UI-CHAKRA/FormControl';
import { Spinner } from './UI-CHAKRA/Spinner';
import { Box } from './UI-CHAKRA/Box';
import { FormErrorMessage } from './UI-CHAKRA/FormErrorMessage';

interface SearchAddressByCepProps {
  onFindAddress(address: Address): void;
  onSearchError?(): void;
  onChange?(value: string): void;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  name?: string;
  error?: boolean;
  errorMessage?: string;
  initialValue?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  required: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
}

function SearchAddressByCep(props: SearchAddressByCepProps, ref: React.Ref<any>) {
  const [search, setSearch] = useState('');
  const [loading, runWithLoading] = useLoading();
  const latestCep = useRef('');

  useEffect(() => {
    latestCep.current = onlyDigits(props.initialValue ?? '');

    if (props.initialValue) {
      setSearch(props.initialValue);
    }
  }, [props.initialValue]);

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = onlyDigits(event.target.value);
    latestCep.current = value;
    setSearch(value);

    if (props.onChange) {
      props.onChange(value);
    }

    if (isString(value) && value.length === 8) {
      searchForAddress(value);
    }
  };

  const onFindAddress = useMemo(() => props.onFindAddress, [props.onFindAddress]);

  const onSearchError = useMemo(() => props.onSearchError, [props.onSearchError]);

  const debouncedSearchForAddress = useMemo(() => {
    return debounce(async (cep: string) => {
      runWithLoading(async () => {
        try {
          const address = await CEPService.searchForCepInViaCep(cep);

          if (address && latestCep.current === cep) {
            onFindAddress(address);
          }
        } catch (e) {
          if (onSearchError && latestCep.current === cep) onSearchError();
        }
      });
    }, 350);
  }, [onFindAddress, onSearchError, runWithLoading]);

  const searchForAddress = useCallback(debouncedSearchForAddress, [debouncedSearchForAddress]);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (props.onBlur) props.onBlur(event);
  };

  return (
    <FormControl isInvalid={props.error}>
      {!props.hideLabel && <FormLabel>{`CEP ${props.required ? '*' : ''}`}</FormLabel>}
      <InputGroup position={'relative'}>
        <MaskedInput
          w={'100%'}
          value={search}
          onChange={onChangeSearch}
          placeholder={'01234-000'}
          mask={MASKS.BRAZIL_POSTAL_CODE}
          maskChar={''}
          data-testid={'input-phone'}
          isDisabled={props.disabled}
          onBlur={handleBlur}
          name={props.name}
        />

        {loading && (
          <Box position={'absolute'} h={'100%'} display={'flex'} alignItems={'center'} right={'10px'} zIndex={1}>
            <Spinner color="primary.main" />
          </Box>
        )}
      </InputGroup>
      {props.errorMessage && <FormErrorMessage>{props.errorMessage}</FormErrorMessage>}
    </FormControl>
  );
}

export default React.memo(React.forwardRef(SearchAddressByCep));
